import { useGetEmailTemplatesQuery } from '@/features/templates/templatesApi';
import { formatDate, toTitleCase } from '@/utils/constants';
import {
	Autocomplete,
	CircularProgress,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	TextField,
	Tooltip,
	Typography,
	Box,
} from '@mui/material';
import { styled, lighten, darken } from '@mui/system';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useGetDealRowQuery } from '@/features/deals/dealsApi';
import Fuse from 'fuse.js';
import { FUSE_OPTIONS } from '@/features/templates/helper';
import { safelyParseDate } from '@/utils/date';

const GroupHeader = styled('div')(({ theme }) => ({
	position: 'sticky',
	top: '-8px',
	padding: '0px 10px',
	color: theme.palette.primary.main,
	backgroundColor: lighten(theme.palette.primary.light, 0.85),
	// ...theme.applyStyles('dark', {
	// 	backgroundColor: darken(theme.palette.primary.main, 0.8),
	// }),
}));

const GroupItems = styled('ul')({
	padding: 0,
});

const EmailTemplateSelect = ({
	value,
	setValue,
	disabled,
	type,
	requirements = {},
	dealId,
	...props
}) => {
	const { data, ...emailTemplatesQuery } = useGetEmailTemplatesQuery({ type: null });

	const { data: dealRow, ...dealRowQuery } = useGetDealRowQuery(dealId, { skip: !dealId });
	const isClaim = dealRow?.isClaim;

	const templates = useMemo(() => {
		const temp = [
			...(data ?? [])
				.filter((item) => {
					if (item?.requirements?.includes('dealId')) {
						return item.dealId && item.dealId === dealId;
					}
					return true;
				})
				.map((item) => {
					const isItemDisabled = () => {
						// console.log('ITEM REQUIREMENTS', item);
						if (item?.requirements.length > 0) {
							return item?.requirements.reduce((acc, req) => {
								const exists = Object.keys(requirements).includes(req);
								acc[req] = exists && !requirements?.[req];
								return acc;
							}, {});
						}
						if (
							['preRenewal', 'scopeOfAdvice', 'statementOfAdvice'].includes(
								item?.type,
							) &&
							isClaim
						) {
							return true;
						}
						return false;
					};
					return {
						...item,
						disabled: isItemDisabled(item),
					};
				}),
			{ id: '', name: 'None', disabled: false, sortBy: '' },
		];
		return temp.sort((a, b) => -b.sortBy.localeCompare(a.sortBy));
	}, [data, requirements, dealId]);

	const templateValue = templates.find((t) => t?.id === value?.id) ?? null;

	useEffect(() => {
		if (!templateValue && value?.id) {
			setValue(undefined);
		}
	}, [templateValue, value, setValue]);

	const searchFuse = useMemo(() => {
		const fuseIndex = Fuse.createIndex(FUSE_OPTIONS.keys, templates);
		return new Fuse(templates, FUSE_OPTIONS, fuseIndex);
	}, [templates]);

	return (
		<>
			<Autocomplete
				fullWidth
				disabled={disabled || !emailTemplatesQuery.isSuccess}
				isOptionEqualToValue={(template, val) => template?.id === val?.id}
				getOptionLabel={(template) => (!template?.id ? '' : (template?.name ?? ''))}
				// options={templates}
				options={templates}
				groupBy={(option) => option.sortBy}
				loading={!emailTemplatesQuery.isSuccess}
				value={templateValue}
				getOptionDisabled={(template) => {
					const itemDisabled =
						typeof template?.disabled == 'boolean'
							? template?.disabled
							: Object.values(template?.disabled ?? {}).some((v) => v);
					return itemDisabled;
				}}
				filterOptions={(options, params) => {
					const search = params.inputValue;
					if (options.length > 0 && search) {
						const searched = searchFuse.search(search).flatMap((v) => v?.item);
						return searched.sort((a, b) => -b.sortBy.localeCompare(a.sortBy));
					}
					return options;
				}}
				onChange={(_, template) => {
					// setTemplateValue(template);
					setValue(template?.id ? template : undefined);
				}}
				renderGroup={(params) => (
					<li key={params.key}>
						<GroupHeader>{params.group}</GroupHeader>
						<GroupItems>{params.children}</GroupItems>
					</li>
				)}
				renderOption={(props, template) => {
					const itemDisabled =
						typeof template?.disabled == 'boolean'
							? template?.disabled
							: Object.values(template?.disabled ?? {}).some((v) => v);
					const values = Object.entries(template?.disabled ?? {})
						.filter(([key, val]) => val)
						.map(([key, val]) => toTitleCase(key).toLowerCase())
						.join(', ');
					return (
						<li {...props} style={{ width: '100%' }}>
							<Tooltip
								key={`email-template-selection-${template.id}`}
								title={
									itemDisabled ? (
										isClaim ? (
											'Template not allowed for claims'
										) : (
											`Template requires the following: ${values}`
										)
									) : template.aiGenerated ? (
										<Stack direction='row' spacing={3}>
											{'Template generated by Folio AI'}
											<AutoAwesomeIcon sx={{ fontSize: '14px' }} />
										</Stack>
									) : (
										''
									)
								}
								sx={{
									width: '100%',
								}}
							>
								<Stack
									direction='row'
									justifyContent='space-between'
									alignItems='start'
									width='100%'
									spacing={1}
								>
									<Box sx={{ flexGrow: 1, minWidth: 0 }}>
										<Typography sx={{ whiteSpace: 'normal' }}>
											{template.name}
										</Typography>
									</Box>
									<Stack
										direction='row'
										spacing={1}
										sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
									>
										{template.createdWhen && (
											<Typography variant='task_updated'>
												{formatDate(safelyParseDate(template.createdWhen))}
											</Typography>
										)}

										{template.aiGenerated && (
											<AutoAwesomeIcon
												fontSize='small'
												sx={(theme) => ({ color: theme.palette.primary.main })}
											/>
										)}
									</Stack>
								</Stack>
							</Tooltip>
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						// onKeyDown={(e) => {
						// 	e.preventDefault();
						// }}
						slotProps={{
							input: {
								...params.InputProps,
								// readOnly: true,

								// style: {
								// 	caretColor: 'transparent',
								// 	cursor: 'pointer',
								// },
								endAdornment: (
									<React.Fragment>
										{!emailTemplatesQuery.isSuccess ? (
											<CircularProgress color='inherit' size={20} />
										) : null}
										{params.InputProps.endAdornment}
									</React.Fragment>
								),

								// cursor: 'pointer',
							},
						}}
						// sx={{
						// 	cursor: 'pointer',
						// }}
					/>
				)}
				// sx={{
				// 	cursor: 'pointer',
				// 	'& .MuiInputBase-root': {
				// 		cursor: 'pointer',
				// 	},
				// 	'& input': {
				// 		cursor: 'pointer',
				// 	},
				// }}
			/>
		</>
	);
};

export default EmailTemplateSelect;
